import type {
    Graph,
    MerchantReturnPolicy,
    MergeLeafTypes,
    OnlineStoreLeaf,
    OrganizationLeaf,
    SearchAction,
    ShippingService,
    WithActionConstraints,
    WithContext,
} from 'schema-dts';

export type OrganizationSchemaType = MergeLeafTypes<[OrganizationLeaf, OnlineStoreLeaf]>;

const ORGANIZATION_LOGO_URL =
    'https://pressrum.coop.se/wp-content/uploads/2016/10/Coop-Produktvarumarke-Logotyp.jpg';

/** Stable ids so PDP offers and the organization schema share one policy graph. */
export const MERCHANT_RETURN_POLICY_ID =
    'https://www.coop.se/handla/kopvillkor/#merchant-return-policy';
export const SHIPPING_HOME_DELIVERY_ID = 'https://www.coop.se/#shipping-home-delivery';
export const SHIPPING_STORE_PICKUP_ID = 'https://www.coop.se/#shipping-store-pickup';

/**
 * Online purchase right of withdrawal (ångerrätt), verified with customer service:
 * 14 days; register on Coop.se; return in store after store coordination, or carrier
 * pickup for home delivery; customer pays return cost. Some goods are excluded
 * (e.g. perishables, sealed hygiene with broken seal) — details on merchantReturnLink.
 * In-store purchases are out of scope for this OnlineStore policy.
 */
export const merchantReturnPolicyJsonLd = {
    '@id': MERCHANT_RETURN_POLICY_ID,
    '@type': 'MerchantReturnPolicy',
    url: 'https://www.coop.se/handla/kopvillkor/',
    merchantReturnLink: 'https://www.coop.se/handla/kopvillkor/',
    applicableCountry: 'SE',
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: 14,
    returnMethod: ['https://schema.org/ReturnInStore', 'https://schema.org/ReturnByMail'],
    returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
    inStoreReturnsOffered: true,
} as const satisfies MerchantReturnPolicy & { '@id': string };

export const shippingHomeDeliveryJsonLd = {
    '@id': SHIPPING_HOME_DELIVERY_ID,
    '@type': 'ShippingService',
    name: 'Kostnad för hemleverans i Sverige',
    shippingConditions: [
        {
            '@type': 'ShippingConditions',
            shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'SE',
            },
            orderValue: {
                '@type': 'MonetaryAmount',
                minValue: 500,
                maxValue: 1999.99,
                currency: 'SEK',
            },
            shippingRate: {
                '@type': 'MonetaryAmount',
                minValue: 49,
                maxValue: 89,
                currency: 'SEK',
            },
            transitTime: {
                '@type': 'ServicePeriod',
                duration: {
                    '@type': 'QuantitativeValue',
                    minValue: 0,
                    maxValue: 2,
                    unitCode: 'DAY',
                },
                businessDays: [
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday',
                ],
            },
        },
        {
            '@type': 'ShippingConditions',
            shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'SE',
            },
            orderValue: {
                '@type': 'MonetaryAmount',
                minValue: 2000,
                currency: 'SEK',
            },
            shippingRate: {
                '@type': 'MonetaryAmount',
                value: 0,
                currency: 'SEK',
            },
            transitTime: {
                '@type': 'ServicePeriod',
                duration: {
                    '@type': 'QuantitativeValue',
                    minValue: 0,
                    maxValue: 2,
                    unitCode: 'DAY',
                },
                businessDays: [
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday',
                ],
            },
        },
    ],
} as const satisfies ShippingService & { '@id': string };

export const shippingStorePickupJsonLd = {
    '@id': SHIPPING_STORE_PICKUP_ID,
    '@type': 'ShippingService',
    name: 'Upphämtning i butik',
    fulfillmentType: 'FulfillmentTypePickupInStore',
    shippingConditions: [
        {
            '@type': 'ShippingConditions',
            shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'SE',
            },
            shippingRate: {
                '@type': 'MonetaryAmount',
                minValue: 0,
                maxValue: 99,
                currency: 'SEK',
            },
            orderValue: {
                '@type': 'MonetaryAmount',
                minValue: 500,
                currency: 'SEK',
            },
        },
    ],
} as const satisfies ShippingService & { '@id': string };

export const shippingServiceJsonLd = [
    shippingHomeDeliveryJsonLd,
    shippingStorePickupJsonLd,
] as const;

/** Return policy + shipping services for the PDP (same @ids the offer references). */
export const merchantPolicyGraphJsonLd: Graph = {
    '@context': 'https://schema.org',
    '@graph': [merchantReturnPolicyJsonLd, ...shippingServiceJsonLd],
};

export const organizationJsonLd: WithContext<OrganizationSchemaType> = {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'OnlineStore'],
    name: 'Coop',
    alternateName: 'Coop Sverige',
    legalName: 'Coop Sverige AB',
    foundingDate: '1899',
    taxID: '556710-5480',
    vatID: 'SE556710548001',
    iso6523Code: '0007:5567105480',
    url: 'https://www.coop.se/',
    logo: ORGANIZATION_LOGO_URL,
    image: ORGANIZATION_LOGO_URL,
    email: 'handla@coop.se',
    telephone: '+46-771-17-17-17',
    description:
        'Coop är en av Sveriges största livsmedelskedjor med cirka 800 butiker från Kiruna i norr till Smygehamn i söder. Coop har också en omfattande e-handel med ett brett sortiment av mat, färskvaror och receptfria läkemedel med hemleverans eller upphämtning i butik. Coops butiker ägs av våra dryg fyra miljoner medlemmar som får personliga erbjudanden, medlemspriser varje vecka och bonus på sina inköp.',
    address: {
        '@type': 'PostalAddress',
        streetAddress: 'Englundavägen 4',
        postalCode: '171 41',
        addressLocality: 'Solna',
        addressCountry: 'SE',
    },

    hasShippingService: [...shippingServiceJsonLd],

    hasMerchantReturnPolicy: merchantReturnPolicyJsonLd,
    contactPoint: [
        {
            '@type': 'ContactPoint',
            telephone: '+46-771-17-17-17',
            contactType: 'customer service',
            areaServed: 'SE',
            availableLanguage: ['Swedish', 'English'],
            url: 'https://www.coop.se/coop-kundservice/',
            hoursAvailable: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '08:00',
                closes: '18:00',
            },
        },
        {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            email: 'handla@coop.se',
            areaServed: 'SE',
            availableLanguage: ['Swedish', 'English'],
            url: 'https://www.coop.se/coop-kundservice/',
        },
    ],
    areaServed: {
        '@type': 'Country',
        name: 'SE',
    },
    // Google sitelinks search box; typed via WithActionConstraints (schema-dts).
    potentialAction: {
        '@type': 'SearchAction',
        target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://www.coop.se/handla/sok?q={search_term_string}',
        },
        'query-input': 'required name=search_term_string',
    } as WithActionConstraints<SearchAction>,
    sameAs: [
        'https://www.facebook.com/CoopSverige/',
        'https://www.instagram.com/coopsverige/',
        'https://www.linkedin.com/company/coopsverige',
        'https://www.youtube.com/user/CoopSverige',
        'https://www.tiktok.com/@coopsverige',
    ],
};
